import { useState, useRef, useEffect } from "react";
import { TbEdit } from "react-icons/tb";
import { BsShare } from "react-icons/bs";
import { FiCopy } from "react-icons/fi";
import DomainModal from "../../components/DomainSettings/DomainModal";
import toast from "react-hot-toast";

import axios from "axios";
import { useSelector } from "react-redux";
import ErrorMessage from "../../components/ErrorMessage";
import { Skeleton } from "@/components/ui/skeleton";
import { Capacitor } from "@capacitor/core";
import { Share } from '@capacitor/share';
import { useTranslation } from "react-i18next";
import useLockBodyScroll from "@/hooks/useLockBodyScroll";


export const CompanyUrl = ".interflow.sa/";

function DomainSettings() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const modalRef = useRef<HTMLDivElement | null>(null);
  const [domainName, setDomainName] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<null | string>(null);
  useLockBodyScroll(isModalOpen);
  const token = useSelector(
    (state: { auth: { token: string } }) => state.auth.token,
  );
  useEffect(() => {
    if (!token) return

    const fetchDomain = async () => {
      const BASE_URL = import.meta.env.VITE_BASE_URL;
      const url = `${BASE_URL}/api/dashboard/settings/subdomain?locale=${lang}`;

      setIsLoading(true);
      setError(null);

      try {
        const response = await axios.get(url, {
          headers: {
            Authorization: `${token}`,
          },
        });
        setDomainName(response.data?.subdomain || "");
      } catch (error: unknown) {
        if (axios.isAxiosError(error)) {
          const message =
            error.response?.data?.error || t("domainSettings.fetchError") as string;
          setError(message);
          toast.error(message);
        } else {
          setError(t("domainSettings.unexpectedError") as string);
          toast.error(t("domainSettings.unexpectedError") as string);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchDomain();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainName, token]);

  // Handle copying the domain link
  const handleCopy = () => {
    const link = `https://${domainName}${CompanyUrl}`;
    navigator.clipboard
      .writeText(link)
      .then(() => toast.success("Link copied to clipboard"))
      .catch(() => toast.error("Error copying link."));
  };

  // Handle sharing the domain link
  const handleShare = async () => {
    const link = `https://${domainName}${CompanyUrl}`;
    if (Capacitor.getPlatform() === "web") {
      if (navigator.share) {
        navigator
          .share({
            title: t("domainSettings.shareLink") as string,
            url: link,
          })
          .catch(() => toast.error(t("domainSettings.shareError") as string))
      } else {
        toast.error(t("domainSettings.notSupported") as string)
      }
    } else {
      await Share.share({
        title: t("domainSettings.shareLink") as string,
        url: link,
      });
    }
  };

  if (isLoading) return <>
    <div className="mt-5">
      <Skeleton className="w-full px-10 dark:bg-slate-600 dark:bg-opacity-35  h-[100px]" />
    </div>
  </>;
  if (error) return <ErrorMessage msg={error} />;

  return (
    <div className="flex flex-col gap-4">
      <h1 className="self-start text-2xl font-semibold">{t("domainSettings.title")}</h1>

      <div className="flex flex-col gap-5 rounded-md border border-gray-300 px-4 py-6">
        {/* Link Bar */}
        <div className="flex min-h-12 w-full flex-wrap justify-between overflow-hidden rounded-md border border-gray-300 lg:flex-nowrap">
          {/* Link Bar Right Side (controls) */}
          <div className="flex size-full min-h-12 items-center justify-between gap-4 border-b border-gray-300 text-gray-400 lg:justify-start lg:border-0">
            <p className="select-none px-2 lg:border-e text-sm lg:border-e-gray-400">
              {t("domainSettings.currentLink")}
            </p>

            {/* Buttons */}
            <div className="flex gap-2 pe-2 lg:pe-0">
              {/* Share */}
              <button onClick={handleShare} className="p-1 hover:text-gray-600">
                <BsShare />
              </button>

              {/* Copy */}
              <button onClick={handleCopy} className="p-1 hover:text-gray-600">
                <FiCopy />
              </button>
            </div>
          </div>

          {/* Link Bar Left Side (domain name and edit) */}
          <div className="flex w-full items-center justify-end">
            <input
              disabled
              type="text"
              readOnly
              value={`https://${domainName}${CompanyUrl}`}
              className="w-full pe-4 text-end underline disabled:bg-white"
            />
            <button
              onClick={() => setIsModalOpen(true)}
              className="flex aspect-square h-full min-h-12 items-center justify-center bg-gray-100 text-xl text-gray-700 hover:text-gray-800"
            >
              <TbEdit />
            </button>
          </div>
        </div>
      </div>

      {isModalOpen && (
        <div
          ref={modalRef}
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50"
          role="dialog"
          aria-modal="true"
        >
          <DomainModal
            currentDomain={domainName}
            onClose={() => setIsModalOpen(false)}
            setDomainName={setDomainName}
          />
        </div>
      )}
    </div>
  );
}

export default DomainSettings;
